// Quotes (round 43, spec 12). The only place the web app handles money.
//
// Amounts arrive as integer CENTS and are only ever turned into dollars for
// display, by `formatCents` below. Rates go back to the server as the text a
// person typed ("12.50"), and the server does the rounding (quotes/money.ts):
// one place that rounds, not two that might disagree by a cent.
import { request } from './api';
import type { ClientRep } from './schedulingApi';

export type QuoteStatus = 'draft' | 'sent' | 'on_hold' | 'won' | 'lost' | 'expired';

/** Every status that can be DISPLAYED, in ladder order. Also the list's tabs. */
export const QUOTE_STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  on_hold: 'On hold',
  won: 'Won',
  lost: 'Lost',
  expired: 'Expired',
};

export interface QuoteSummary {
  id: string;
  number: string;
  title: string;
  status: QuoteStatus;
  clientId: string;
  clientName: string;
  quoteManagerId: string | null;
  quoteManagerName: string | null;
  totalCents: number;
  currentRevision: number;
  quoteDate: string;
  decidedDate: string | null;
  submissionDueDate: string | null;
  converted: boolean;
  convertedJobId: string | null;
  convertedJobNumber: string | null;
  archived: boolean;
  isSampleData: boolean;
}

export interface QuoteLine {
  id: string;
  description: string;
  unit: string | null;
  quantity: string;
  rateCents: number;
  lineTotalCents: number;
}

export interface QuoteRevision {
  id: string;
  revisionNo: number;
  title: string;
  siteAddress: string | null;
  description: string | null;
  exclusions: string | null;
  validUntil: string | null;
  gstApplicable: boolean;
  subtotalCents: number;
  gstCents: number;
  totalCents: number;
  sentDate: string | null;
  createdByName: string;
  createdAt: string;
}

export interface QuoteNote {
  id: string;
  quoteId: string;
  body: string;
  createdByName: string;
  createdAt: string;
}

export interface QuoteEvent {
  id: string;
  kind:
    | 'created'
    | 'status_changed'
    | 'revised'
    | 'converted'
    | 'dates_corrected'
    | 'archived'
    | 'unarchived'
    // Round 45: what people do to a quote day to day.
    | 'details_changed'
    | 'prices_changed'
    | 'note_added'
    | 'file_added'
    | 'file_removed';
  fromStatus: QuoteStatus | null;
  toStatus: QuoteStatus | null;
  revisionNo: number | null;
  detail: Record<string, unknown>;
  actorName: string;
  createdAt: string;
}

export interface QuoteDetail {
  quote: QuoteSummary;
  clientRepId: string | null;
  rep: ClientRep | null;
  lockVersion: number;
  revision: QuoteRevision;
  lines: QuoteLine[];
  revisions: QuoteRevision[];
  notes: QuoteNote[];
  events: QuoteEvent[];
  /** Names for the ids a history entry points at, as they read now (round 45). */
  eventNames: Record<string, string>;
  /** The server's own answer to "where can this go next" (lib/quoteStatus.ts). */
  allowedStatuses: QuoteStatus[];
  can: { edit: boolean; revise: boolean; convert: boolean; delete: boolean };
}

export interface QuoteReport {
  from: string;
  to: string;
  summary: {
    quoted: number;
    won: number;
    lost: number;
    expired: number;
    wonCents: number;
    converted: number;
    /** Null when nothing was decided, rather than a misleading 0%. */
    winRate: number | null;
  };
  byMonth: { month: string; quoted: number; quotedCents: number; won: number; wonCents: number }[];
  byManager: { managerId: string | null; managerName: string | null; won: number; lost: number; wonCents: number }[];
  pipeline: { managerId: string | null; managerName: string | null; draft: number; sent: number; onHold: number; cents: number }[];
  byClient: { clientId: string; clientName: string; count: number; cents: number; won: number; wonCents: number }[];
}

export interface LineInput {
  description: string;
  unit: string;
  quantity: string;
  rate: string;
}

export interface QuoteEdit {
  lockVersion: number;
  clientId?: string;
  title?: string;
  siteAddress?: string | null;
  description?: string | null;
  exclusions?: string | null;
  validUntil?: string | null;
  clientRepId?: string | null;
  quoteManagerId?: string | null;
  submissionDueDate?: string | null;
}

export const quotesApi = {
  list: (options: {
    status?: string;
    search?: string;
    clientId?: string;
    managerId?: string;
    archived?: boolean;
    offset?: number;
  } = {}) => {
    const params = new URLSearchParams();
    if (options.offset) params.set('offset', String(options.offset));
    if (options.status) params.set('status', options.status);
    if (options.search) params.set('search', options.search);
    if (options.clientId) params.set('clientId', options.clientId);
    if (options.managerId) params.set('managerId', options.managerId);
    if (options.archived) params.set('archived', 'true');
    const query = params.toString();
    return request<{ quotes: QuoteSummary[]; total: number; totalCents: number }>(
      `/quotes${query ? `?${query}` : ''}`
    );
  },
  get: (id: string) => request<QuoteDetail>(`/quotes/${id}`),
  revision: (id: string, no: number) =>
    request<{ revision: QuoteRevision; lines: QuoteLine[] }>(`/quotes/${id}/revisions/${no}`),
  create: (body: {
    clientId: string;
    title: string;
    siteAddress?: string | null;
    description?: string | null;
    submissionDueDate?: string | null;
    quoteManagerId?: string | null;
  }) => request<QuoteDetail>('/quotes', { method: 'POST', body }),
  update: (id: string, body: QuoteEdit) =>
    request<QuoteDetail>(`/quotes/${id}`, { method: 'PATCH', body }),
  saveLines: (id: string, lockVersion: number, lines: LineInput[]) =>
    request<QuoteDetail>(`/quotes/${id}/lines`, { method: 'PUT', body: { lockVersion, lines } }),
  setStatus: (id: string, status: QuoteStatus) =>
    request<QuoteDetail>(`/quotes/${id}/status`, { method: 'POST', body: { status } }),
  revise: (id: string) => request<QuoteDetail>(`/quotes/${id}/revise`, { method: 'POST' }),
  archive: (id: string) => request<QuoteDetail>(`/quotes/${id}/archive`, { method: 'POST' }),
  unarchive: (id: string) => request<QuoteDetail>(`/quotes/${id}/unarchive`, { method: 'POST' }),
  remove: (id: string) => request<void>(`/quotes/${id}`, { method: 'DELETE' }),
  correctDates: (
    id: string,
    body: { quoteDate?: string; sentDate?: string | null; decidedDate?: string | null }
  ) => request<QuoteDetail>(`/quotes/${id}/dates`, { method: 'PATCH', body }),
  /** Makes the job. Only the files named are copied to it (round 43). */
  convert: (id: string, attachmentIds: string[]) =>
    request<{ jobId: string; jobNumber: string }>(`/quotes/${id}/convert`, {
      method: 'POST',
      body: { attachmentIds },
    }),
  /** Admin only (round 43, release 4). The whole range in one request. */
  reports: (from: string, to: string) =>
    request<QuoteReport>(`/quote-reports?from=${from}&to=${to}`),
  /** Admin only. All or nothing; the server names every bad row. */
  importRows: (rows: Record<string, string>[]) =>
    request<{ imported: number }>('/quotes/import', { method: 'POST', body: { rows } }),
  addNote: (id: string, body: string) =>
    request<{ note: QuoteNote }>(`/quotes/${id}/notes`, { method: 'POST', body: { body } }),
};

/**
 * Cents for display: "$1,250.50", "−$0.50" for a credit. Always two places,
 * because a quote is read by the cent and "$12.5" reads as a typo.
 */
export function formatCents(cents: number): string {
  const formatted = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(Math.abs(cents) / 100);
  return cents < 0 ? `-${formatted}` : formatted;
}

/** Cents back to the plain text a rate box holds: 1250 -> "12.50". */
export function centsToInput(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const magnitude = Math.abs(cents);
  return `${sign}${Math.trunc(magnitude / 100)}.${String(magnitude % 100).padStart(2, '0')}`;
}
